import { memo } from "react";
import { MediasLogo } from "~/features/navigation/components/TopBar/MediasLogo";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { CART_PAGE, REGISTER_PAGE } from "~/utils/url/staticUrls";
import { Trans, useTranslation } from "react-i18next";
import { Box, Button, styled } from "~/common/mui";
import { type SalesPartnerSearchFormValues, trackPartnerFinderSearch } from "~/app/analytics/ga/salesPartner";
import type { GoogleMarker } from "~/domains/salesPartner/types/SalesPartnerTypes";
import { transformSelectedMarkerData } from "~/features/cart/B2CRequestPage/utils";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

const Container = styled(Box)(({ theme }) => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  justifyContent: "center",
  alignItems: "stretch",
  marginTop: theme.spacing(3),
  gap: theme.spacing(2),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const SPCompanyDetailsCardRightUnregistered = (props: {
  company: GoogleMarker;
  searchValues: SalesPartnerSearchFormValues;
}) => {
  const isDesktop = useIsMinDesktopSize();
  const { t } = useTranslation();

  const trackRegister = () => {
    trackPartnerFinderSearch(
      props.searchValues,
      "Register Intended",
      "Click",
      transformSelectedMarkerData(t, props.company),
    );
  };

  const trackGoToCart = () => {
    trackPartnerFinderSearch(
      props.searchValues,
      "Go To Cart Intended",
      "Click",
      transformSelectedMarkerData(t, props.company),
    );
  };

  if (isDesktop) {
    return <Desktop trackRegister={trackRegister} trackGoToCart={trackGoToCart} />;
  }

  return null;
};

const Desktop = (props: { trackRegister: () => void; trackGoToCart: () => void }) => {
  const { t } = useTranslation();
  return (
    <Container>
      <div className="scha__sales-partner__gmaps-sp-card__right__header__img-container">
        <MediasLogo variant={"plus"} />
      </div>
      <h3 className="title-large">{t("salesPartner.gmap.card.rightUnregistered.title")}</h3>
      <div className="scha__sales-partner__gmaps-sp-card__right__register">
        <div className="scha__sales-partner__gmaps-sp-card__right__register__instruction">
          <ol className="scha__sales-partner__gmaps-sp-card__right__register__instruction__list">
            <li>
              <Trans i18nKey={"salesPartner.gmap.card.rightRegistered.new.1step"} />
            </li>
            <li>
              <Trans i18nKey={"salesPartner.gmap.card.rightRegistered.new.2step"} />
            </li>
            <li>
              <Trans i18nKey={"salesPartner.gmap.card.rightRegistered.new.3step"} />
            </li>
          </ol>
          <ButtonContainer>
            <Button
              startIcon={<PersonOutlineOutlinedIcon />}
              fullWidth={true}
              data-test-id={"sales-partner-register-for-free"}
              href={REGISTER_PAGE}
              onClick={props.trackRegister}
            >
              {t("page.registerFree.title")}
            </Button>
            <Button
              href={CART_PAGE}
              startIcon={<ShoppingCartOutlinedIcon />}
              color="secondary"
              onClick={props.trackGoToCart}
            >
              {t("addToCartPopup.checkoutBtn")}
            </Button>
          </ButtonContainer>
        </div>
      </div>
    </Container>
  );
};

export default memo(SPCompanyDetailsCardRightUnregistered);
